<template>
	<v-tabs
		direction="vertical"
		hide-slider
		:height="tabHeight"
		v-model="activeTab"
		@click="selectDateTime">
		<v-tab v-for="d in dateTimes" :key="d.format()" class="justify-center"
			:class="{'with-playlist': hasPlaylist(d), current: isCurrent(d)}">
			{{ d.format("HH") }}
		</v-tab>
	</v-tabs>
</template>

<script>
import moment from "moment-timezone";

export default {
	name: "Clock",

	props: [
		"selectedStation", "stationCurrentHour", "selectedDateTime", "from", "to", "scrollbar", "tabHeight",
		"sequencerShowEpisodeID"
	],

	emits: ["selectDateTime"],

	data: () => ({
		dateTimes: [],
		activeTab: null
	}),

	computed: {
		timezone() {
			return this.selectedStation?.StationTimeZone ?? moment.tz.guess();
		},

		// Start of the hour (epoch ms) of every loaded playlist of the station. It is calculated once per store change,
		// so that rendering the hour tabs only needs a Set lookup instead of parsing dates for every tab.
		playlistHours() {
			const hours = new Set();
			const stationID = this.selectedStation?.StationID;
			if (!stationID) {
				return hours;
			}
			Object.values(this.$store.getters.loadedPlaylistsNoAssetsByStationID(stationID)).forEach(playlists => {
				playlists?.forEach(p => {
					hours.add(moment.tz(p.ScheduledStartDateTime, this.timezone).startOf("hour").valueOf());
				});
			});
			return hours;
		},

		showEpisodePlaylist() {
			if (this.sequencerShowEpisodeID) {
				return this.$store.getters.loadedShowEpisodePlaylists(
					this.selectedStation.StationID,
					this.sequencerShowEpisodeID
				);
			}
			return null;
		}
	},

	methods: {
		selectDateTime() {
			this.$emit("selectDateTime", this.dateTimes[this.activeTab].startOf("hour"));
		},

		setDates() {
			this.dateTimes = [];
			let from = this.from ? this.selectedDateTime.clone().subtract(this.from, "hours") :
			           this.selectedDateTime.clone().startOf("day");
			const to = this.to ? this.selectedDateTime.clone().add(this.to, "hours") :
			           this.selectedDateTime.clone().endOf("day");
			while (from.isSameOrBefore(to)) {
				this.dateTimes.push(from.clone());
				from.add(1, "hour");
			}
		},

		// Scroll to the selected date-hour
		scrollToSelectedDateTime() {
			if (this.scrollbar) {
				this.activeTab = this.dateTimes.findIndex(dt => dt.isSame(this.selectedDateTime));
				const scrollToTabIndex = this.dateTimes?.findIndex(dt => dt.isSame(this.selectedDateTime)) - 11;

				const parentEl = this.$parent?.$refs?.scrollbar?.$el ||
				                 this.$parent?.$refs?.rootCard?.$el ||
				                 this.$parent?.$el;
				if (parentEl) {
					parentEl.scrollTo({
						top: (scrollToTabIndex > 0 ? scrollToTabIndex : 0) * this.tabHeight,
						left: 0
					});
				}
			}
		},

		dateInTimeRange(date) {
			return date.isBetween(this.showEpisodePlaylist.start, this.showEpisodePlaylist.end, undefined, '[]');
		},

		isCurrent(date) {
			if (this.showEpisodePlaylist) {
				return this.dateInTimeRange(date);
			}
			return this.stationCurrentHour.isSame(moment.tz(date, this.timezone), "hour");
		},

		hasPlaylist(date) {
			return this.playlistHours.has(date.clone().startOf("hour").valueOf());
		},

		async loadPlaylistsForVisibleRange() {
			if (!this.selectedStation) {
				return;
			}

			const stationID = this.selectedStation.StationID;
			for (let dateTime of this.dateTimes) {
				const from = dateTime.clone().startOf("week");
				const to = dateTime.clone().endOf("week");
				const dateRange = `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;

				// If a request for this date range has already been initiated, continue with the next week.
				if (!this.$store.getters.playlistsNoAssetsLoadedByDateRange(stationID, dateRange)) {
					await this.loadPlaylistsNoAssets(from, to);
				}
			}
		},

		loadPlaylistsNoAssets(from, to) {
			const stationID = this.selectedStation.StationID;
			const dateRange = `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;
			// Set the key in the store as an identifier that a request for this date range has already been initiated.
			this.$store.commit("addLoadedPlaylistNoAssets", {stationID, dateRange});

			return this.$store.dispatch(
						   "getPlaylistBySchedule",
						   {stationID, from, to, params: {ReturnChildEvents: false}}
					   )
			           .then(response => {
						   const dateRange = `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`;
						   const playlists = response.data;
						   this.$store.commit("addLoadedPlaylistNoAssets", {stationID, dateRange, playlists});

						   return playlists;
					   })
			           .catch(() => {
						   this.$store.commit(
							   "removeLoadedPlaylistNoAssets",
							   {stationID, dateRange: `${from.format("YYYY-MM-DD")}_${to.format("YYYY-MM-DD")}`}
						   );
					   });
		}
	},

	mounted() {
		this.setDates();
		this.$nextTick(this.scrollToSelectedDateTime);
	},

	watch: {
		selectedDateTime() {
			this.setDates();
			this.$nextTick(this.scrollToSelectedDateTime);
		},

		dateTimes() {
			this.loadPlaylistsForVisibleRange();
		},

		selectedStation: {
			handler() {
				this.loadPlaylistsForVisibleRange();
			},
			immediate: true
		}
	}
}
</script>

<style lang="scss" scoped>
.v-tabs {
	border-radius: 0 0 0 6px;
	max-width: 24px;

	.v-tab {
		border-left: 3px solid $ihm-red-error;
		font-size: 12px;
		font-weight: 700;
		line-height: 14px;
		letter-spacing: 0;
		min-width: 24px !important;
		max-width: 24px !important;
		padding: 0 3px 0 0;
	}
}
</style>